import type { EntryConditions, Timeframe } from "../types";
import { formatPrice } from "../lib/format";
import { timeframeReadouts } from "../lib/signals";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { cn } from "@/lib/utils";

interface Props {
  data: EntryConditions | null;
}

const LABEL: Record<"below" | "within" | "above", string> = {
  below: "↓ BELOW",
  within: "WITHIN",
  above: "↑ ABOVE",
};

export function BbPanel({ data }: Props) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-baseline justify-between space-y-0 border-b p-3">
        <CardTitle>Bollinger Bands</CardTitle>
        <span className="text-[11px] text-muted-foreground">price vs bands</span>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-1.5 p-2">
        {data ? (
          timeframeReadouts(data).map((r) => (
            <BbCell
              key={r.timeframe}
              timeframe={r.timeframe}
              zone={r.bbZone}
              upper={r.upper}
              middle={r.middle}
              lower={r.lower}
              price={data.current_price}
            />
          ))
        ) : (
          <div className="col-span-2 p-2 italic text-muted-foreground">
            Loading…
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BbCell({
  timeframe,
  zone,
  upper,
  middle,
  lower,
  price,
}: {
  timeframe: Timeframe;
  zone: "below" | "within" | "above";
  upper: number;
  middle: number;
  lower: number;
  price: number;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 rounded-md border p-2 transition-colors",
        zone === "below" && "border-buy/35 bg-buy/10",
        zone === "above" && "border-sell/35 bg-sell/10",
        zone === "within" && "border-transparent bg-secondary"
      )}
    >
      <div className="flex items-baseline justify-between">
        <span className="font-mono-tabular text-[10px] text-muted-foreground">
          {timeframe}
        </span>
        <span
          className={cn(
            "font-mono-tabular text-[10px] font-semibold tracking-wider",
            zone === "below" && "text-buy",
            zone === "above" && "text-sell",
            zone === "within" && "text-foreground"
          )}
        >
          {LABEL[zone]}
        </span>
      </div>

      <BbBar price={price} upper={upper} lower={lower} />

      <div className="flex justify-between font-mono-tabular text-[10px] text-muted-foreground">
        <span>{formatPrice(lower)}</span>
        <span className="opacity-60">{formatPrice(middle)}</span>
        <span>{formatPrice(upper)}</span>
      </div>
    </div>
  );
}

function BbBar({
  price,
  upper,
  lower,
}: {
  price: number;
  upper: number;
  lower: number;
}) {
  const span = upper - lower;
  const raw = span > 0 ? (price - lower) / span : 0.5;
  const pct = Math.max(0, Math.min(1, raw));
  const over = raw > 1 ? "above" : raw < 0 ? "below" : null;
  return (
    <div className="relative h-3 w-full" aria-hidden="true">
      <div
        className="absolute left-0 right-0 top-1/2 h-0.5 -translate-y-1/2 rounded-sm"
        style={{
          background:
            "linear-gradient(to right, hsl(var(--buy)) 0%, hsl(var(--neutral)) 50%, hsl(var(--sell)) 100%)",
        }}
      />
      <div
        className={cn(
          "absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full ring-1 ring-background shadow",
          over === "above" && "bg-sell",
          over === "below" && "bg-buy",
          over === null && "bg-foreground"
        )}
        style={{
          left:
            over === "above"
              ? "calc(100% - 5px)"
              : over === "below"
              ? "-5px"
              : `calc(${pct * 100}% - 5px)`,
        }}
      />
    </div>
  );
}
